// External component imports
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import { Grid } from '@interstate/components/Grid';

// Constants and types
import { COMPLIANCE_CONSISTENCY_CHECK_MESSAGE, comparisonValues } from '../../shared/constants';

// Local style imports
import { ContainerBox, useStyles } from '../ComplianceAccordian.style';

// Local utility imports
import * as complianceSelectors from '@makemydeal/dr-dash-store/selectors/complianceSelectors';
import { useSelector } from 'react-redux';
import { StatusValue } from '../../shared/types';
import { StatusIcon } from '../../shared/StatusIcon';
import { Alert } from '@interstate/components/Alert';

export const ConsistencyCheckDetails = () => {
    const classes = useStyles();
    const piiConsistencyData = useSelector(complianceSelectors.getComplianceParticipantPIIData);
    const piiConsistencyStatus = useSelector(complianceSelectors.getPIIConsistencyStatus);
    const piiConsistencyLoading = useSelector(complianceSelectors.getPIIConsistencyLoading);
    const piiConsistencyError = useSelector(complianceSelectors.getPIIConsistencyErrorMessage);
    const inconsistentFields = useSelector(complianceSelectors.getInconsistentPIIFields);

    // Handle loading state
    if (piiConsistencyLoading) {
        return (
            <ContainerBox>
                <Box display="flex" alignItems="center" gap={2}>
                    <StatusIcon status={StatusValue.Undetermined} />
                    <Typography variant="body-sm">Loading PII consistency check...</Typography>
                </Box>
            </ContainerBox>
        );
    }

    // Handle error state
    if (piiConsistencyError) {
        return (
            <ContainerBox>
                <Alert type="error" data-testid="pii-consistency-error-alert" title="Error">
                    <Typography variant="body-sm">
                        An error occurred while checking PII consistency. Please try again later.
                    </Typography>
                </Alert>
            </ContainerBox>
        );
    }

    // Handle no data (shouldn't happen if error handling is correct, but good fallback)
    if (!piiConsistencyData) {
        return (
            <ContainerBox>
                <Alert type="caution" data-testid="pii-consistency-no-data-alert" title="No Data Available">
                    <Typography variant="body-sm">
                        No PII consistency data available. Please try refreshing or contact support if the issue persists.
                    </Typography>
                </Alert>
            </ContainerBox>
        );
    }

    // Map status to StatusValue enum
    const getStatusValue = (status: string): StatusValue => {
        switch (status) {
            case 'PASS':
                return StatusValue.Passed;
            case 'FAIL':
                return StatusValue.Failed;
            case 'LOADING':
            case 'ERROR':
            default:
                return StatusValue.Undetermined;
        }
    };

    const status = piiConsistencyStatus;
    const statusValue = getStatusValue(status);

    if (status === 'PASS') {
        const message =
            COMPLIANCE_CONSISTENCY_CHECK_MESSAGE.MESSAGES[status as keyof typeof COMPLIANCE_CONSISTENCY_CHECK_MESSAGE.MESSAGES];

        return (
            <ContainerBox>
                <Box display="flex" alignItems="center" gap={2}>
                    <StatusIcon status={statusValue as StatusValue.Passed | StatusValue.Failed | StatusValue.Undetermined} />
                    {message.title && <Typography variant="strong-sm">{message.title}</Typography>}
                </Box>
                {message.description && (
                    <Box marginTop={2}>
                        <Typography variant="body-sm">{message.description}</Typography>
                    </Box>
                )}
            </ContainerBox>
        );
    }

    // For FAIL cases, show "Provided information is not matching" with grid format
    if (status === 'FAIL') {
        // Extract dynamic column names from the first inconsistent field
        const getColumnNames = (fields: string[], data: any): string[] => {
            if (fields.length === 0) return [];

            const firstField = fields[0];
            const fieldData = data[firstField];

            return fieldData?.values ? Object.keys(fieldData.values) : [];
        };

        const columnNames = getColumnNames(inconsistentFields, piiConsistencyData);

        // Format field names for display (firstName -> First Name, lastName -> Last Name, etc.)
        const formatFieldName = (name: string): string => {
            return name
                .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
                .trim();
        };

        return (
            <ContainerBox>
                <Box display="flex" alignItems="center" gap={2}>
                    <StatusIcon status={statusValue as StatusValue.Passed | StatusValue.Failed | StatusValue.Undetermined} />
                    <Typography variant="strong-sm">Provided information is not matching</Typography>
                </Box>

                {/* Show inconsistent fields in grid format */}
                {inconsistentFields.length > 0 && columnNames.length > 0 && (
                    <Box marginTop={3}>
                        {/* Table Header */}
                        <Grid container spacing={1} style={{ marginBottom: '8px' }}>
                            <Grid xs={3}>
                                <Typography variant="strong-xs" color="text.secondary">
                                    Field
                                </Typography>
                            </Grid>
                            {columnNames.map((columnName) => (
                                <Grid key={`header-${columnName}`} xs={Math.floor(9 / columnNames.length)}>
                                    <Typography variant="strong-xs" color="text.secondary">
                                        {comparisonValues[columnName] || columnName.charAt(0).toUpperCase() + columnName.slice(1)}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Table Body */}
                        {inconsistentFields.map((fieldName, index) => {
                            const fieldData = piiConsistencyData[fieldName as keyof typeof piiConsistencyData];

                            return (
                                <Grid
                                    key={`field-row-${fieldName}`}
                                    container
                                    spacing={1}
                                    className={classes.container}
                                    data-testid={`inconsistent-field-${fieldName}`}
                                    style={{
                                        padding: '8px 0',
                                        borderBottom: index === inconsistentFields.length - 1 ? 'none' : '1px solid #f0f0f0'
                                    }}
                                >
                                    {/* Field Name */}
                                    <Grid xs={3}>
                                        <Typography variant="strong-sm">{formatFieldName(fieldName)}</Typography>
                                    </Grid>

                                    {/* Dynamic Value Cells */}
                                    {columnNames.map((columnName) => (
                                        <Grid key={`${fieldName}-${columnName}`} xs={Math.floor(9 / columnNames.length)}>
                                            <Typography variant="body-sm">
                                                {fieldData?.values?.[columnName as keyof typeof fieldData.values] || ''}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            );
                        })}
                    </Box>
                )}
            </ContainerBox>
        );
    }

    // Fallback for any other status
    const message =
        COMPLIANCE_CONSISTENCY_CHECK_MESSAGE.MESSAGES[status as keyof typeof COMPLIANCE_CONSISTENCY_CHECK_MESSAGE.MESSAGES] || '';

    return (
        <ContainerBox>
            <Box display="flex" alignItems="center" gap={2}>
                <StatusIcon status={statusValue as StatusValue.Passed | StatusValue.Failed | StatusValue.Undetermined} />
                {message.title && <Typography variant="strong-sm">{message.title}</Typography>}
            </Box>

            <Box marginTop={2}>{message.description && <Typography variant="body-sm">{message.description}</Typography>}</Box>
        </ContainerBox>
    );
};
